import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { timerocketApi } from '../../services/timerocketApi';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import type { DayCategory, DayRow, DaysContract, DaysResponse, HistoryRow, HoursAccount } from '../../types/timerocket';
import { formatDays, formatMinutes, formatRange, formatWorkload, weekdayShort } from './format';

/**
 * The daily detail of ONE person for the page's current period: every workday with presence /
 * actual / target and the day balance, the contract those targets come from, and the balance
 * history (month-end rows plus the latest) so a vacation figure can be followed back in time.
 *
 * A day the categories know but the hours do not (`hasDailyRow: false` — a lagging hours
 * backfill) is listed too, italic, with "—" for its minutes: null is "no figure", never a zero,
 * and such a day stays out of the footer sums.
 *
 * Who gets here is decided by the server (the person themself or HR); the table only offers the
 * click to those rows. A 403 still shows the server's reason rather than an empty modal.
 *
 * Both tables follow the table standard with their own width storage keys, so a column dragged
 * in the days table does not move one in the history table.
 */

interface Props {
    isOpen: boolean;
    onClose: () => void;
    employeeId: string;
    name: string;
    from: string;
    to: string;
    /** From the list response — the days endpoint returns ids only, the labels live in the catalog. */
    accounts: HoursAccount[];
}

const FILTER_LABELS = { search: 'Search…', all: 'All', none: 'None', from: 'from', to: 'to', empty: '(blank)' };

export function DaysModal({ isOpen, onClose, employeeId, name, from, to, accounts }: Props) {
    const [data, setData] = useState<DaysResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setData(null);
        timerocketApi.days({ employeeId, from, to })
            .then(r => { if (!cancelled) setData(r); })
            .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load the daily detail'))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [isOpen, employeeId, from, to]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        if (isOpen) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-3 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">{name} — {formatRange(from, to)}</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Daily detail from the nightly TimeRocket sync</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" title="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-5 space-y-4">
                    {loading && (
                        <div className="p-8 text-center text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                        </div>
                    )}
                    {error && (
                        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{error}</div>
                    )}
                    {!loading && !error && data && (
                        <>
                            <ContractBox contract={data.contract} />
                            <DaysTable days={data.days} />
                            <HistoryTable history={data.history} accounts={accounts} />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

// ---------------------------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------------------------

const WEEKDAYS: { key: keyof DaysContract['workingHours']; label: string }[] = [
    { key: 'mo', label: 'Mo' }, { key: 'tu', label: 'Tu' }, { key: 'we', label: 'We' }, { key: 'th', label: 'Th' },
    { key: 'fr', label: 'Fr' }, { key: 'sa', label: 'Sa' }, { key: 'su', label: 'Su' },
];

function ContractBox({ contract }: { contract: DaysContract | null }) {
    if (!contract) {
        return <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">No contract synced for this person.</div>;
    }
    return (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 flex flex-wrap items-center gap-x-5 gap-y-1">
            <span><span className="text-gray-500">Workload</span> <span className="font-medium tabular-nums">{formatWorkload(contract.workload)}</span></span>
            <span className="inline-flex items-center gap-2">
                <span className="text-gray-500">Target per weekday</span>
                {WEEKDAYS.map(d => (
                    <span key={d.key} className="tabular-nums">
                        <span className="text-gray-400">{d.label}</span> {formatMinutes(contract.workingHours[d.key])}
                    </span>
                ))}
            </span>
            <span title="Minutes that make one day for the day-based accounts (vacation)">
                <span className="text-gray-500">Conversion rate</span>{' '}
                <span className="font-medium tabular-nums">
                    {contract.conversionRate === null ? 'none' : `${formatMinutes(contract.conversionRate)} per day`}
                </span>
            </span>
            {contract.validFrom && <span className="text-gray-400">valid from {contract.validFrom}</span>}
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// Days table
// ---------------------------------------------------------------------------------------------

type DayKey = 'workday' | 'presenceMin' | 'actualMin' | 'targetMin' | 'balanceMin' | 'categories' | 'isProvisional';

const DAY_COLUMNS: { key: DayKey; label: string; width: number; align?: 'right' }[] = [
    { key: 'workday', label: 'Date', width: 150 },
    { key: 'presenceMin', label: 'Presence', width: 100, align: 'right' },
    { key: 'actualMin', label: 'Actual', width: 100, align: 'right' },
    { key: 'targetMin', label: 'Target', width: 100, align: 'right' },
    { key: 'balanceMin', label: 'Balance', width: 100, align: 'right' },
    { key: 'categories', label: 'Type', width: 240 },
    { key: 'isProvisional', label: 'Status', width: 110 },
];

/** "Ferien 4:12" — the chip text; also what the Type column sorts and filters on. */
function categoryText(c: DayCategory): string {
    return `${c.label} ${formatMinutes(c.minutes)}`;
}

const DAY_ACCESSOR: Record<DayKey, (r: DayRow) => string | number | null> = {
    workday: r => r.workday,
    presenceMin: r => r.presenceMin,
    actualMin: r => r.actualMin,
    targetMin: r => r.targetMin,
    balanceMin: r => r.balanceMin,
    // An API deployed before P3 answers without `categories` (feedback_pwa_stale_bundle_after_deploy
    // cuts both ways); the guard keeps such a response from blanking the whole table.
    categories: r => ((r.categories ?? []).length === 0 ? null : (r.categories ?? []).map(categoryText).join(', ')),
    isProvisional: r => (r.isProvisional ? 'provisional' : 'synced'),
};

const DAY_NUMERIC: DayKey[] = ['presenceMin', 'actualMin', 'targetMin', 'balanceMin'];

const DAY_FILTER_COLUMNS = DAY_COLUMNS.map(c => ({
    key: c.key,
    accessor: DAY_ACCESSOR[c.key],
    numeric: DAY_NUMERIC.includes(c.key),
}));

function DaysTable({ days }: { days: DayRow[] }) {
    const [sort, setSort] = useState<SortState<DayKey>>({ key: 'workday', direction: 'asc' });
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(DAY_COLUMNS, 'timerocket-days-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(days, DAY_FILTER_COLUMNS);

    const onSort = (key: DayKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, DAY_NUMERIC.includes(key)));
    };

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = DAY_ACCESSOR[sort.key as DayKey];
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    // Category-only days carry null minutes and contribute nothing — the footer keeps meaning
    // "the days the hours know", not "the days plus a few invented zeros".
    const sums = filtered.reduce(
        (s, r) => ({
            presence: s.presence + (r.presenceMin ?? 0), actual: s.actual + (r.actualMin ?? 0),
            target: s.target + (r.targetMin ?? 0), balance: s.balance + (r.balanceMin ?? 0),
        }),
        { presence: 0, actual: 0, target: 0, balance: 0 },
    );

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-900">Days</div>
            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{activeCount} filter(s) active</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">Clear all</button>
                </div>
            )}
            {/* The sticky header needs a bounded scroll container or it does not stick. */}
            <div className="overflow-auto" style={{ maxHeight: '45vh' }}>
                {days.length === 0 && (
                    <div className="p-8 text-center text-gray-500">No workdays synced for this period.</div>
                )}
                {days.length > 0 && filtered.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{activeCount} filter(s) active — no row matches.</div>
                )}
                {days.length > 0 && filtered.length > 0 && (
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {DAY_COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {DAY_COLUMNS.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={c.label}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={c.label}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={DAY_NUMERIC.includes(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={FILTER_LABELS}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(r => {
                                // Only an explicit false marks the row: an API deployed before this contract
                                // answers without `hasDailyRow`, and a stale bundle must not italicise every day.
                                const categoryOnly = r.hasDailyRow === false;
                                return (
                                <tr
                                    key={r.workday}
                                    className={`${r.isProvisional ? 'bg-amber-50/40' : 'hover:bg-gray-50'} ${categoryOnly ? 'italic text-gray-500' : ''}`}
                                    title={categoryOnly ? 'no daily-time row — category only' : undefined}
                                >
                                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                                        {r.workday} <span className="text-gray-400">{weekdayShort(r.workday)}</span>
                                    </td>
                                    {/* formatMinutes renders null as "—": a category-only day has no minutes, not zero minutes. */}
                                    <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.presenceMin)}</td>
                                    <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.actualMin)}</td>
                                    <td className="px-3 py-1.5 text-right tabular-nums">{formatMinutes(r.targetMin)}</td>
                                    <td className={`px-3 py-1.5 text-right tabular-nums ${r.balanceMin !== null && r.balanceMin < 0 ? 'text-red-600' : ''}`}>{formatMinutes(r.balanceMin)}</td>
                                    <td className="px-3 py-1.5">
                                        {/* One chip per time type of the day; the label is the tenant's own, the group
                                            (FEIERTAG, KRANKHEIT, …) only in the title — the chips stay one neutral colour so
                                            a sick day is not the one that jumps off the screen. */}
                                        <div className="flex flex-wrap gap-1">
                                            {(r.categories ?? []).map(c => (
                                                <span
                                                    key={c.timeTypeId}
                                                    className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 whitespace-nowrap tabular-nums"
                                                    title={c.group ?? 'unclassified'}
                                                >
                                                    {categoryText(c)}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {r.isProvisional && (
                                            <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                                                  title="Today's value before the nightly sync — it will still change">
                                                provisional
                                            </span>
                                        )}
                                        {categoryOnly && (
                                            <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs not-italic text-gray-600"
                                                  title="no daily-time row — category only">
                                                category only
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t font-medium">
                            <tr>
                                <td className="px-3 py-2">
                                    {activeCount > 0 ? `${filtered.length} of ${days.length} days` : `${days.length} days`}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.presence)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.actual)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(sums.target)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${sums.balance < 0 ? 'text-red-600' : ''}`}>{formatMinutes(sums.balance)}</td>
                                <td />
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// History table — one row per as-of date, one column per account
// ---------------------------------------------------------------------------------------------

interface HistoryPivotRow {
    asOfDate: string;
    isMonthEnd: boolean;
    byAccount: Record<string, HistoryRow>;
}

type HistoryKey = 'asOfDate' | 'kind' | `acct:${string}`;

interface HistoryColDef {
    key: HistoryKey;
    label: string;
    width: number;
    align?: 'right';
    account?: HoursAccount;
}

const HISTORY_FIXED: HistoryColDef[] = [
    { key: 'asOfDate', label: 'As of', width: 120 },
    { key: 'kind', label: 'Kind', width: 110 },
];

/** Same display unit as the list table: days where the account is shown as days, else minutes. */
function historyValue(row: HistoryPivotRow, account: HoursAccount): number | null {
    const h = row.byAccount[account.accountId];
    if (!h) return null;
    return account.showAsDays ? h.days : h.totalMin;
}

function HistoryTable({ history, accounts }: { history: HistoryRow[]; accounts: HoursAccount[] }) {
    const [sort, setSort] = useState<SortState<HistoryKey>>({ key: 'asOfDate', direction: 'desc' });

    const sortedAccounts = useMemo(
        () => [...accounts].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
        [accounts],
    );

    // The server delivers one row per (date, account); the table wants one row per date. An
    // account the catalog does not know gets no column — it has no label to head it with.
    const rows = useMemo<HistoryPivotRow[]>(() => {
        const byDate = new Map<string, HistoryPivotRow>();
        for (const h of history) {
            const r = byDate.get(h.asOfDate) ?? { asOfDate: h.asOfDate, isMonthEnd: h.isMonthEnd, byAccount: {} };
            r.byAccount[h.accountId] = h;
            r.isMonthEnd = r.isMonthEnd || h.isMonthEnd;
            byDate.set(h.asOfDate, r);
        }
        return [...byDate.values()];
    }, [history]);

    const columns = useMemo<HistoryColDef[]>(() => [
        ...HISTORY_FIXED,
        ...sortedAccounts.map<HistoryColDef>(a => ({ key: `acct:${a.accountId}`, label: a.label, width: 110, align: 'right', account: a })),
    ], [sortedAccounts]);

    const accessor = useMemo(() => {
        const byKey = new Map<HistoryKey, (r: HistoryPivotRow) => string | number | null>();
        byKey.set('asOfDate', r => r.asOfDate);
        byKey.set('kind', r => (r.isMonthEnd ? 'month-end' : 'latest'));
        for (const c of columns) {
            if (c.account) {
                const account = c.account;
                byKey.set(c.key, r => historyValue(r, account));
            }
        }
        return (key: HistoryKey) => byKey.get(key) ?? (() => null);
    }, [columns]);

    const isNumeric = (key: HistoryKey) => key.startsWith('acct:');

    const filterColumns = useMemo(
        () => columns.map(c => ({ key: c.key, accessor: accessor(c.key), numeric: isNumeric(c.key) })),
        [columns, accessor],
    );

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(columns, 'timerocket-history-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, filterColumns);

    const onSort = (key: HistoryKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, isNumeric(key)));
    };

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = accessor(sort.key as HistoryKey);
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-900">
                Balance history
                <span className="ml-2 font-normal text-gray-500">month-end stands and the latest one</span>
            </div>
            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{activeCount} filter(s) active</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">Clear all</button>
                </div>
            )}
            {/* The sticky header needs a bounded scroll container or it does not stick. */}
            <div className="overflow-auto" style={{ maxHeight: '35vh' }}>
                {rows.length === 0 && (
                    <div className="p-8 text-center text-gray-500">No balances synced yet.</div>
                )}
                {rows.length > 0 && filtered.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{activeCount} filter(s) active — no row matches.</div>
                )}
                {rows.length > 0 && filtered.length > 0 && (
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {columns.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {columns.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={c.label}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={c.label}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={isNumeric(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={FILTER_LABELS}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(r => (
                                <tr key={r.asOfDate} className={r.isMonthEnd ? 'font-medium hover:bg-gray-50' : 'text-gray-600 hover:bg-gray-50'}>
                                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{r.asOfDate}</td>
                                    <td className="px-3 py-1.5">
                                        {r.isMonthEnd
                                            ? <span className="inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">month-end</span>
                                            : <span className="text-xs text-gray-400">latest</span>}
                                    </td>
                                    {sortedAccounts.map(a => {
                                        const h = r.byAccount[a.accountId];
                                        if (!h) return <td key={a.accountId} className="px-3 py-1.5 text-right text-gray-400">—</td>;
                                        if (a.showAsDays) {
                                            return h.days === null ? (
                                                <td key={a.accountId} className="px-3 py-1.5 text-right tabular-nums text-gray-500" title="no conversion rate">
                                                    {h.totalMin} min
                                                </td>
                                            ) : (
                                                <td key={a.accountId} className={`px-3 py-1.5 text-right tabular-nums ${h.days < 0 ? 'text-red-600' : ''}`}>
                                                    {formatDays(h.days)}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={a.accountId} className={`px-3 py-1.5 text-right tabular-nums ${h.totalMin < 0 ? 'text-red-600' : ''}`}>
                                                {formatMinutes(h.totalMin)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
